import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { AtSign } from "lucide-react";

/**
 * Phase 64 — MentionTextarea
 *
 * Textarea with @mention autocomplete against /api/team/members.
 * Typing "@" opens a floating picker; selecting a teammate inserts their
 * display name into the body and pushes their user_id into `mentions`.
 *
 * Props:
 *   value        (string)               — controlled body value
 *   onChange     (nextValue) => void    — receives raw text
 *   onMentions   (userIds:[]) => void   — called whenever the mention list changes
 *   placeholder, className, ...rest      — forwarded to <Textarea>
 */
export default function MentionTextarea({
    value, onChange, onMentions,
    placeholder = "Add a comment. Type @ to mention a teammate.",
    className = "rounded-none text-sm min-h-[72px]",
    autoFocus = false,
    testid,
}) {
    const [members, setMembers] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerQuery, setPickerQuery] = useState("");
    const [hi, setHi] = useState(0);
    const [mentionsMap, setMentionsMap] = useState({}); // {display_name: user_id}
    const areaRef = useRef(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/team/members", { params: { limit: 40 } });
                if (alive) setMembers(data?.items || []);
            } catch { /* silent */ }
        })();
        return () => { alive = false; };
    }, []);

    // Whenever value changes, recompute active mentions by scanning "@Name"
    // tokens against our known members. mention_ids passed up as flat array.
    useEffect(() => {
        const found = new Set();
        const map = { ...mentionsMap };
        for (const m of members) {
            const nm = (m.name || "").trim();
            if (!nm) continue;
            if (value.includes(`@${nm}`)) {
                found.add(m.id);
                map[nm] = m.id;
            }
        }
        setMentionsMap(map);
        onMentions?.(Array.from(found));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, members]);

    const filtered = pickerQuery
        ? members.filter((m) => (m.name || m.email || "").toLowerCase().includes(pickerQuery.toLowerCase()))
        : members;

    const handleChange = (e) => {
        const next = e.target.value;
        onChange(next);
        // Look at the last "@..." token; open the picker if it's active.
        const caret = e.target.selectionStart || next.length;
        const before = next.slice(0, caret);
        const match = before.match(/@([A-Za-z0-9_.\-\s]{0,30})$/);
        if (match) {
            setPickerOpen(true);
            setPickerQuery(match[1] || "");
            setHi(0);
        } else {
            setPickerOpen(false);
        }
    };

    const insertMention = (m) => {
        const area = areaRef.current;
        if (!area) return;
        const caret = area.selectionStart || value.length;
        const before = value.slice(0, caret);
        const after = value.slice(caret);
        const cleaned = before.replace(/@([A-Za-z0-9_.\-\s]{0,30})$/, "");
        const nm = m.name || m.email || "teammate";
        const nextText = `${cleaned}@${nm} ${after}`;
        onChange(nextText);
        setPickerOpen(false);
        // Move caret past the inserted mention on the next tick.
        setTimeout(() => {
            const pos = (cleaned + "@" + nm + " ").length;
            area.focus();
            area.setSelectionRange(pos, pos);
        }, 0);
    };

    const onKeyDown = (e) => {
        if (!pickerOpen) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, filtered.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
        else if (e.key === "Enter" && filtered[hi]) { e.preventDefault(); insertMention(filtered[hi]); }
        else if (e.key === "Escape") { setPickerOpen(false); }
    };

    return (
        <div className="relative">
            <Textarea
                ref={areaRef}
                value={value}
                onChange={handleChange}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className={className}
                autoFocus={autoFocus}
                data-testid={testid}
            />
            {pickerOpen && filtered.length > 0 && (
                <div
                    className="absolute left-0 right-0 sm:max-w-xs top-full mt-1 bg-white border border-border shadow-xl z-40 max-h-60 overflow-y-auto"
                    data-testid="mention-picker"
                >
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono text-muted-foreground bg-secondary/50 flex items-center gap-1.5">
                        <AtSign className="w-3 h-3" /> Mention a teammate
                    </div>
                    <ul>
                        {filtered.slice(0, 8).map((m, i) => (
                            <li key={m.id}>
                                <button
                                    type="button"
                                    onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                                    className={`w-full text-left px-3 py-2 border-l-2 ${i === hi ? "border-ikb bg-blue-50/50" : "border-transparent hover:bg-secondary/60"}`}
                                    data-testid={`mention-option-${m.id}`}
                                >
                                    <div className="text-sm font-medium">{m.name || m.email}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{m.role}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
